<template>
    <div>
        <progress-header />

        <div class="container mt-4">
            <div class="row">
                <form class="col-12 col-lg-9 mb-2" @submit.prevent="handleSubmit">
                    <div class="card booking-form-card shadow-sm">
                        <div class="card-header">
                            {{ $t('taxi.pages.addresses.header') }}
                        </div>

                        <div class="px-4 py-3">
                            <div class="text-lg text-blue-600 font-weight-bold">
                                {{ $t('taxi.pages.addresses.pickup_addresses') }}
                            </div>

                            <div class="mt-3 pb-4 border-bottom" v-for="(address, idx) in addresses(addressTypes.pickup)" :key="address.id">
                                <address-form
                                    :address="address"
                                    :idx="idx"
                                    :is-airport="address.is_airport"
                                    :show-flight-number="address.is_airport"
                                    :type="addressTypes.pickup"
                                    @update="onAddressUpdated"
                                    @remove="onAddressRemove"
                                />
                            </div>

                            <div class="mt-2" v-if="addresses(addressTypes.pickup).length < 2">
                                <a href="#" class="text-sm" @click.prevent="handleAddressAdd(addressTypes.pickup)">
                                    {{ $t('taxi.pages.addresses.address_add') }}
                                </a>
                            </div>
                        </div>

                        <div class="px-4 py-3">
                            <div class="text-lg text-blue-600 font-weight-bold">
                                {{ $t('taxi.pages.addresses.destination_addresses') }}
                            </div>

                            <div class="mt-3 pb-4 border-bottom" v-for="(address, idx) in addresses(addressTypes.destination)" :key="address.id">
                                <address-form
                                    :address="address"
                                    :idx="idx"
                                    :is-airport="address.is_airport"
                                    :show-flight-number="address.is_airport"
                                    :type="addressTypes.destination"
                                    @update="onAddressUpdated"
                                    @remove="onAddressRemove"
                                />
                            </div>

                            <div class="mt-2" v-if="addresses(addressTypes.destination).length < 2">
                                <a href="#" class="text-sm" @click.prevent="handleAddressAdd(addressTypes.destination)">
                                    {{ $t('taxi.pages.addresses.address_add') }}
                                </a>
                            </div>
                        </div>

                        <div>
                            <div class="mt-3 px-4 py-3 border-top border-bottom">
                                <h4 class="text-blue-600 font-weight-bold">
                                    {{ $t('taxi.pages.addresses.return_travel') }}
                                </h4>
                            </div>

                            <div class="row justify-content-center p-4" v-if="!returnChecked">
                                <div class="spinner-border" role="status">
                                    <span class="sr-only">{{ $t('loading') }}...</span>
                                </div>
                            </div>
                            <div v-else>
                                <div class="px-4 py-3" v-if="returnPossible">
                                    <div class="pt-2 pb-3 mb-3 border-bottom">
                                        <label>
                                            <input type="checkbox" class="mr-2" :checked="hasReturnTravel"
                                                @change="onHasReturnTravelInput"
                                            >

                                            {{ $t('taxi.pages.addresses.book_return') }}

                                            <span class="ml-2 text-blue-600 font-weight-bold" v-if="returnTravel">
                                                +{{ formatPrice(returnTravel.gross_price, returnTravel.currency) }}
                                            </span>
                                        </label>
                                    </div>

                                    <div class="my-4" v-if="hasReturnTravel">
                                        <h4 class="text-lg text-blue-600 font-weight-bold">
                                            {{ $t('taxi.return_datetime') }}
                                        </h4>

                                        <date-picker
                                            type="date"
                                            :placeholder="$t('taxi.return_date')"
                                            style="border:none"
                                            value-type="format"
                                            :disabled-date="notBeforeDeparture"
                                            :value="searchParams.return_date"
                                            @change="handleReturnDateChange"
                                        />

                                        <vue-timepicker
                                            :minute-interval="5"
                                            :placeholder="$t('taxi.return_time')"
                                            hide-clear-button
                                            v-model="searchParams.return_time"
                                            @change="handleReturnTimeChange"
                                        />
                                    </div>

                                    <div v-if="hasReturnTravel">
                                        <div class="text-lg text-blue-600 font-weight-bold">
                                            {{ $t('taxi.pages.addresses.return_pickup_addresses') }}
                                        </div>

                                        <div class="mt-3 pb-4 border-bottom" v-for="(address) in addresses(addressTypes.destination)" :key="address.id">
                                            <address-form
                                                :address="address"
                                                :idx="-1"
                                                :lock-address="true"
                                                :is-airport="address.is_airport"
                                                :comments-for-return="true"
                                                :show-flight-number="false"
                                                :show-flight-number-return="true"
                                                :type="addressTypes.return_pickup"
                                                @update="onAddressUpdated"
                                            />
                                        </div>
                                    </div>

                                    <div v-if="hasReturnTravel">
                                        <div class="mt-3 text-lg text-blue-600 font-weight-bold">
                                            {{ $t('taxi.pages.addresses.return_destination_addresses') }}
                                        </div>

                                        <div class="mt-3 pb-4 border-bottom" v-for="(address) in addresses(addressTypes.pickup)" :key="address.id">
                                            <address-form
                                                :address="address"
                                                :idx="-1"
                                                :lock-address="true"
                                                :is-airport="address.is_airport"
                                                :comments-for-return="true"
                                                :show-flight-number="false"
                                                :show-flight-number-return="true"
                                                :type="addressTypes.return_destination"
                                                @update="onAddressUpdated"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="px-4 py-3" v-if="!returnPossible">
                                    <div class="mt-2" v-html="$t('taxi.pages.addresses.return_not_possible', { email: officeEmail })" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="my-4">
                        <button type="submit" class="btn btn-primary">
                            {{ $t('next') }}
                        </button>
                    </div>
                </form>

                <div class="col-12 col-lg-3">
                    <the-cart />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    import TheCart from './components/Cart/TheCart';
    import ProgressHeader from './components/ProgressHeader';
    import AddressForm from '@components/Taxi/AddressForm';
    import DatePicker from 'vue2-datepicker';
    import VueTimepicker from 'vue2-timepicker';
    import toastr from 'toastr';
    import {trackGA} from '@utilities/tracking';
    import backofficeRepository from '@repositories/BackofficeRepository';
    import backofficeMapsRepository from '@repositories/BackofficeMapsRepository';
    import {getCurrency} from '@utilities/helpers';

    const today = new Date();
    export default {
        components: {
            ProgressHeader,
            TheCart,
            AddressForm,
            DatePicker,
            VueTimepicker,
        },

        data() {
            return {
                returnChecked: false,
                returnPossible: false,
            };
        },

        computed: {
            ...mapGetters({
                searchParams: 'familyAirportTaxi/searchParameters',
                travel: 'familyAirportTaxi/getTravel',
                returnTravel: 'familyAirportTaxi/getReturnTravel',
                addresses: 'familyAirportTaxi/getAddresses',
                hasReturnTravel: 'familyAirportTaxi/hasReturnTravel',
                officeEmail: 'constants/officeEmail',
                addressTypes: 'constants/addressTypes',
                passengersNumber: 'familyAirportTaxi/passengersNumber',
            }),
        },

        created() {
            trackGA('checkout_progress', {
                checkout_step: 4,
                checkout_option: window.location.pathname,
            });

            if (this.addresses(this.addressTypes.pickup).length === 0) {
                let address = this.addressModel(this.addressTypes.pickup);

                address.street = this.travel.start_place;
                if (this.travel.start_is_airport) {
                    address.is_airport = true;
                    address.zip = '-';
                }

                this.$store.dispatch('familyAirportTaxi/addAddress', address);
            }

            if (this.addresses(this.addressTypes.destination).length === 0) {
                let address = this.addressModel(this.addressTypes.destination);

                address.street = this.travel.end_place;
                if (this.travel.end_is_airport) {
                    address.is_airport = true;
                    address.zip = '-';
                }

                this.$store.dispatch('familyAirportTaxi/addAddress', address);
            }

            // Search for return travel
            this.getTravelVehicle().then(( vehicleTravel ) => {
                this.returnPossible = vehicleTravel !== null;
                this.returnChecked = true;
                this.findReturnTravel();
            });
        },

        methods: {
            validate() {
                let result = true;

                if (this.hasReturnTravel && !this.searchParams.return_date) {
                    toastr.error(this.$t('taxi.validation.return_date_required'));
                    result = false;
                }

                if (this.hasReturnTravel && !this.searchParams.return_time) {
                    toastr.error(this.$t('taxi.validation.return_time_required'));
                    result = false;
                }

                if (this.hasReturnTravel && this.searchParams.return_date && this.searchParams.return_time) {
                    let return_date = new Date(this.searchParams.return_date + ' ' + this.formatTimeObject(this.searchParams.return_time));
                    let departure_date = new Date(this.searchParams.departure_date + ' ' + this.searchParams.departure_time);

                    if (this.minutesBetween(return_date, departure_date) < 180) { // 3h margin (FM-1289)
                        toastr.error(this.$t('taxi.validation.return_time_after_departure_time'));
                        result = false;
                    }
                }

                return result;
            },

            handleSubmit() {
                if (this.validate()) {
                    this.$router.push({name: 'smart-taxi.options'});
                }
            },

            handleAddressAdd(type) {
                let address = this.addressModel(type);
                address.is_airport = false;
                address.flight_number = null;
                this.$store.dispatch('familyAirportTaxi/addAddress', address);
            },

            onAddressUpdated(data) {
                this.$store.dispatch('familyAirportTaxi/updateAddress', data);
            },

            onAddressRemove(address) {
                this.$store.dispatch('familyAirportTaxi/removeAddress', address.id);
            },

            onHasReturnTravelInput(event) {
                const value = event.target.checked;
                this.$store.dispatch('familyAirportTaxi/setHasReturnTravel', value);
            },

            addressModel(type) {
                return {
                    type,
                    street: null,
                    zip: null,
                    comment: null,
                    flight_number: null,
                    is_airport: false,
                };
            },

            notBeforeDeparture(date) {
                if (this.searchParams.departure_date) {
                    let departure_date = new Date(this.searchParams.departure_date);

                    date.setHours(0);
                    departure_date.setHours(0);

                    return date < departure_date;
                }
                return date < today;
            },

            findReturnTravel() {
                if (!this.hasReturnTravel || !this.searchParams.return_date || !this.searchParams.return_time)
                {
                    this.$store.dispatch('familyAirportTaxi/setReturnTravel', null);
                    return;
                }

                this.getTravelVehicle().then(( travelVehicle ) => {
                    if (travelVehicle !== null) {
                        this.$store.dispatch('familyAirportTaxi/setReturnTravel', travelVehicle);
                    }
                });
            },

            getTravelVehicle() {
                const promises = [backofficeRepository.searchMapPlaceDetails(this.searchParams.start_place_id), backofficeRepository.searchMapPlaceDetails(this.searchParams.end_place_id)];
                return Promise.all(promises).then((responses) => {
                    const start_place_coords = {lat: responses[0].locationLatitude,lng: responses[0].locationLongitude};
                    const end_place_coords = {lat: responses[1].locationLatitude, lng: responses[1].locationLongitude};

                    return backofficeRepository.findPreferredVehiclesPrices('FAMILY_AIRPORT_TRANSPORT', getCurrency(), end_place_coords, start_place_coords, this.searchParams.departure_date + ' ' + this.searchParams.departure_time).then((data) => {
                        let ret = data.vehicles.filter(vehicle => vehicle.vehicleId === this.travel.vehicle_id);
                        ret = backofficeMapsRepository.BOVehiclesToTravels(ret, this.searchParams, this.passengersNumber, true);
                        return ret.length ? ret[0] : null;
                    });
                });
            },

            handleReturnDateChange(date) {
                this.$store.dispatch('familyAirportTaxi/setSearchParameter', {field: 'return_date', value: date})
                    .then(() => {
                        this.findReturnTravel();
                    });
            },

            handleReturnTimeChange(change) {
                this.$store.dispatch('familyAirportTaxi/setSearchParameter', {field: 'return_time', value: change.data})
                    .then(() => {
                        this.findReturnTravel();
                    });
            },
        },
    };
</script>

